import React, { useEffect, useState, useRef, Children } from 'react';
import { motion } from 'framer-motion';
import { MapPinIcon, LayoutGridIcon, WrenchIcon, ChevronRightIcon, ChevronLeftIcon } from 'lucide-react';
interface ProjectDetailProps {
  title: string;
  gridLineColor?: string;
}
export function YohanaPersonalAssistant({
  title,
  gridLineColor = 'rgba(185,210,214,0.5)'
}: ProjectDetailProps) {
  const scrollRef1 = useRef<HTMLDivElement>(null);
  const [atEnd1, setAtEnd1] = useState(false);
  const checkScrollEnd = (el: HTMLDivElement) => {
    return el.scrollLeft + el.clientWidth >= el.scrollWidth - 10;
  };
  useEffect(() => {
    const el = scrollRef1.current;
    if (!el) return;
    const handleScroll = () => {
      setAtEnd1(checkScrollEnd(el));
    };
    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, []);
  const scrollRight = (ref: React.RefObject<HTMLDivElement>) => {
    if (ref.current) {
      ref.current.scrollBy({
        left: 380,
        behavior: 'smooth'
      });
    }
  };
  const scrollLeft = (ref: React.RefObject<HTMLDivElement>) => {
    if (ref.current) {
      ref.current.scrollBy({
        left: -380,
        behavior: 'smooth'
      });
    }
  };
  // Scroll to top when mounting
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const container = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1
      }
    }
  };
  const item = {
    hidden: {
      opacity: 0,
      y: 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.19, 1, 0.22, 1]
      }
    }
  };
  return <motion.article variants={container} initial="hidden" animate="show" className="w-full pb-[0px]" data-id="element-582" data-mp="1ki4hwflattmw">
      {/* Header Section */}
      <div className="max-w-4xl mx-auto px-6 md:px-12 mb-16 md:mb-24 text-center pt-12" data-id="element-583" data-mp="3evnd3irux051">
        <motion.h1 variants={item} className="text-4xl md:text-6xl font-bold text-ink tracking-tight mb-6" data-id="element-584" data-mp="3et9kdgkng00n">
          {title}
        </motion.h1>
        <motion.p variants={item} className="text-xl md:text-2xl text-stone-medium font-light leading-relaxed max-w-3xl mx-auto mb-16" data-id="element-585" data-mp="38f3wxk0koonp">
          Led the 0→1 design and launch of Yohana, a subscription-based personal
          assistant service helping busy families delegate tasks and manage
          mental load.
        </motion.p>

        {/* Metadata Row */}
        <motion.div variants={item} className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left border-t border-b border-stone-border py-8" data-id="element-586" data-mp="3r5v3kdt6e0ye">
          <div data-id="element-587" data-mp="3me3ek7po43ad">
            <h3 className="text-sm font-bold text-ink mb-2" data-id="element-588" data-mp="3mdll1iue4365">Scope</h3>
            <p className="text-stone-medium text-sm" data-id="element-589" data-mp="3iecok67ytr6w">0→1 Product Launch</p>
          </div>
          <div data-id="element-590" data-mp="iql9nszfw21p">
            <h3 className="text-sm font-bold text-ink mb-2" data-id="element-591" data-mp="ir3az40yl6gh">Platform</h3>
            <p className="text-stone-medium text-sm" data-id="element-592" data-mp="dzl82afdkvsv">iOS, Android, Web App</p>
          </div>
          <div data-id="element-593" data-mp="1wmcqvzaeou8j">
            <h3 className="text-sm font-bold text-ink mb-2" data-id="element-594" data-mp="1wmuwvnmvemtn">Timeline</h3>
            <p className="text-stone-medium text-sm" data-id="element-595" data-mp="21l68a2std5dz">2021 - 2022</p>
          </div>
        </motion.div>
      </div>

      {/* Hero Image */}
      <motion.div variants={item} className="w-full mb-16 md:mb-24 bg-[#EEF7E4]" style={{
      backgroundImage: `linear-gradient(${gridLineColor} 1px, transparent 1px), linear-gradient(90deg, ${gridLineColor} 1px, transparent 1px)`,
      backgroundSize: '21px 21px'
    }} data-id="element-596" data-mp="25lq1wwe6hqvd">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-597" data-mp="2kjifvk42u77g">
          <div className="w-full rounded-t-2xl overflow-hidden relative flex items-center justify-center" data-id="element-598" data-mp="2khxmhxti0ub6">
            <img src="https://cdn.magicpatterns.com/uploads/n8sc77EwyTZrSNGdSHNLbp/Yohana_hero.png" alt="Yohana Personal Assistant Hero" className="w-full h-full object-cover object-center" data-id="element-599" data-mp="2pd4kqq4016sp" />
          </div>
        </div>
      </motion.div>

      {/* Paragraph Only Modules */}
      <div className="max-w-3xl mx-auto px-6 md:px-12 mb-24 space-y-16" data-id="element-600" data-mp="33b1llyphjzd6">
        <motion.section variants={item} data-id="element-601" data-mp="2pmrfgi4mw0mn">
          <h2 className="text-2xl font-bold text-ink mb-4" data-id="element-602" data-mp="2plq0w3oesw06">Context</h2>
          <p className="text-stone-medium leading-relaxed mb-6" data-id="element-603" data-mp="2kny7t6c133ue">
            Yohana, a Panasonic-backed startup, set out to build a life
            management service across parenting, aging, and home
            logistics—combining humans, AI, and software into one system. We
            partnered closely with product and engineering to bring this vision
            to life.
          </p>
        </motion.section>

        <motion.section variants={item} data-id="element-604" data-mp="21rorecdnm178">
          <h2 className="text-2xl font-bold text-ink mb-4" data-id="element-605" data-mp="2gnnut9ptjd61">Challenge</h2>

          <ul className="list-disc pl-5 text-stone-medium space-y-4" data-id="element-606" data-mp="2gkryfptmcwjp">
            <li data-id="element-607" data-mp="2bmgpdhb5eq2j">
              <span className="font-bold text-ink" data-id="element-608" data-mp="2bq4du21qkmae">
                Broad users and use cases
              </span>
              <br data-id="element-609" data-mp="3n36vz1jhhjwo" />
              With multiple segments and service directions under consideration,
              the product lacked a clear focal point.
            </li>
            <li data-id="element-610" data-mp="3ulejrwdcldpr">
              <span className="font-bold text-ink" data-id="element-611" data-mp="3up2c4s723u2w">
                From vision to product foundation
              </span>
              <br data-id="element-612" data-mp="3lmb1uvpx34cu" />
              Yohana had a compelling vision but no defined product structure —
              no flows, IA, or interaction model to anchor the experience. Our
              role was to build that foundation from the ground up.
            </li>
          </ul>
        </motion.section>
      </div>

      {/* Approach Section — Full Bleed Image */}
      <motion.section variants={item} className="mb-32" data-id="element-613" data-mp="m90ickcoxba4">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center mb-12" data-id="element-614" data-mp="8egb10hzac8e">
          <h2 className="text-3xl font-bold text-ink mb-4" data-id="element-615" data-mp="cgl1jqd4qsqt">Approach</h2>
        </div>
        <div className="w-full flex items-center justify-center bg-[#F5F5F5] pt-[12px] pb-[12px]" data-id="element-616" data-mp="106p5jyu8cuic">
          <img src="https://cdn.magicpatterns.com/uploads/6tJNiWzcaWzCpkSPfjJXGm/Process.png" alt="Approach overview — Discover, Define, Develop, Deliver" className="w-full max-w-[1434px] h-auto block px-3 md:px-6 object-contain" data-id="element-617" data-mp="14xdvvau6blfw" />
        </div>
      </motion.section>

      {/* Paragraph with Image Modules */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 mb-32 space-y-32" data-id="element-618" data-mp="15sfzhdyeas41">
        <motion.section variants={item} className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center" data-id="element-619" data-mp="15uixol9pbrju">
          <div className="lg:col-span-4" data-id="element-620" data-mp="vui84hl8of77">
            <h2 className="text-2xl font-bold text-ink mb-4" data-id="element-621" data-mp="vvjuvjbmaq4r">Discovery</h2>
            <p className="text-stone-medium leading-relaxed" data-id="element-622" data-mp="r39zvovnazvm">
              We synthesized landscape insights, behavioral journeys, and member
              interviews to clarify pain points and support needs. This reframed
              the opportunity around older millennials overwhelmed by work and
              family responsibilities—shifting from broad life support to
              focused task offloading.
            </p>
          </div>
          <div className="lg:col-span-8 w-full overflow-hidden" data-id="element-623" data-mp="85pljbqibjp9">
            <div className="relative" data-id="element-624" data-mp="3u76dc53d1lcm">
              <div ref={scrollRef1} className="flex overflow-x-auto gap-6 pb-6 snap-x snap-mandatory hide-scrollbar" data-id="element-625" data-mp="3u64zjsuymkr7">
                <div className="flex-none w-[280px] sm:w-[360px] snap-start flex flex-col gap-3" data-id="element-626" data-mp="31wsssy4xzae">
                  <div className="w-full aspect-[4/3] bg-stone-light rounded-md border border-stone-border overflow-hidden flex items-center justify-center" data-id="element-627" data-mp="7xc4be8o7j3n">
                    <img src="https://cdn.magicpatterns.com/uploads/wfYsPcS9A65MjSb5n5R588/Member_journeys.png" alt="Member journeys" className="w-full h-full object-cover" data-id="element-628" data-mp="3bh8aq4ic0kgd" />
                  </div>
                  <p className="text-sm text-stone-medium font-medium" data-id="element-629" data-mp="3ldba52lsu4zm">
                    Member journeys
                  </p>
                </div>
                <div className="flex-none w-[280px] sm:w-[360px] snap-start flex flex-col gap-3" data-id="element-630" data-mp="2spfhqa39iz8n">
                  <div className="w-full aspect-[4/3] bg-stone-light rounded-md border border-stone-border overflow-hidden flex items-center justify-center" data-id="element-631" data-mp="2xoihbf0h4d98">
                    <img src="https://cdn.magicpatterns.com/uploads/n7m81Bao8HyCn6yB1ZnMx7/Member_interview.png" alt="Member interviews" className="w-full h-full object-cover" data-id="element-632" data-mp="fq306kvlh75e" />
                  </div>
                  <p className="text-sm text-stone-medium font-medium" data-id="element-633" data-mp="pp22zmtoi8kh">
                    Member interviews
                  </p>
                </div>
                <div className="flex-none w-[280px] sm:w-[360px] snap-start flex flex-col gap-3" data-id="element-634" data-mp="ynx6us9936km">
                  <div className="w-full aspect-[4/3] bg-stone-light rounded-md border border-stone-border overflow-hidden flex items-center justify-center" data-id="element-635" data-mp="undee1zmgr4t">
                    <img src="https://cdn.magicpatterns.com/uploads/jyMtgenpWUsG6THvZNayx3/Concept_testing.png" alt="Concept testing" className="w-full h-full object-cover" data-id="element-636" data-mp="18gcotpkjrv5i" />
                  </div>
                  <p className="text-sm text-stone-medium font-medium" data-id="element-637" data-mp="1iabrcpoj9gxz">
                    Concept testing
                  </p>
                </div>
              </div>
              <button onClick={() => atEnd1 ? scrollLeft(scrollRef1) : scrollRight(scrollRef1)} className="absolute right-2 top-[calc(((100%-24px)*0.5)-20px)] w-10 h-10 rounded-full border border-stone-border bg-white flex items-center justify-center shadow-sm hover:shadow-md transition-shadow cursor-pointer z-10" aria-label={atEnd1 ? 'Scroll left' : 'Scroll right'} data-id="element-638" data-mp="1x0g2tlh5wn1k">
                {atEnd1 ? <ChevronLeftIcon className="w-5 h-5 text-ink" data-id="element-639" data-mp="1davv6ect9kv4" /> : <ChevronRightIcon className="w-5 h-5 text-ink" data-id="element-640" data-mp="1d91ejc8koewu" />}
              </button>
            </div>
          </div>
        </motion.section>

        {/* Discovery — Text + Single Image */}
        <motion.section variants={item} className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center" data-id="element-641" data-mp="148cxe8nvtyfq">
          <div className="lg:col-span-4" data-id="element-642" data-mp="1976bo3azttgy">
            <h2 className="text-2xl font-bold text-ink mb-4" data-id="element-643" data-mp="uc061hft7wo5">Define</h2>
            <p className="text-stone-medium leading-relaxed" data-id="element-644" data-mp="zbczwm53m408">
              I defined an interaction model where conversation is the primary
              experience, with tasks supporting it. This made ownership clearer,
              reduced cognitive load, and aligned the experience with the
              product’s core value: delegation.
            </p>
          </div>
          <div className="lg:col-span-8 w-full" data-id="element-645" data-mp="g3d7h6lq57o1">
            <div className="w-full rounded-md overflow-hidden" data-id="element-646" data-mp="kzk3o7obj4ss">
              <img src="https://cdn.magicpatterns.com/uploads/oWoQRMarPmLezJ4aVsENNx/Core_experience-new.png" alt="Key insight visualization" className="w-full h-auto" data-id="element-647" data-mp="18pxww1in39oy" />
            </div>
          </div>
        </motion.section>
      </div>

      {/* Full Bleed Image Module */}
      <motion.section variants={item} className="mb-32" data-id="element-648" data-mp="yzuw3f5a0rg2">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center mb-12" data-id="element-649" data-mp="13yfura4vr32e">
          <h2 className="text-3xl font-bold text-ink mb-4" data-id="element-650" data-mp="1rp22f0az2lbv">Develop</h2>
          <p className="text-stone-medium mb-8" data-id="element-651" data-mp="1rmpd222vx8os">
            The interaction foundation informed navigation, feature
            prioritization, and visual language.
          </p>
        </div>
        <div className="w-full border-y border-stone-border pt-0 pb-12 flex overflow-visible bg-[#D8DCEE] items-start justify-center relative" data-id="element-652" data-mp="3h3unll0exvm7">
          <img src="https://cdn.magicpatterns.com/uploads/murgWUovPpyyGWxh2MHspT/To-do-transparent.png" alt="Yohana app screens — To-do list, task details, and review items" className="w-full h-auto block px-6 md:px-12 -mb-32 md:-mb-44 relative z-10" style={{
          maxWidth: '86.4rem'
        }} data-id="element-653" data-mp="3qw8qwi753aah" />
        </div>
      </motion.section>

      {/* Deliver Section — Text + Single Image */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 mb-32 mt-[200px]" data-id="element-654" data-mp="377hscip7z9pc">
        <motion.section variants={item} className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center" data-id="element-655" data-mp="375ewhi1dymcp">
          <div className="lg:col-span-4" data-id="element-656" data-mp="mhipuoqy1su4">
            <h2 className="text-2xl font-bold text-ink mb-4" data-id="element-657" data-mp="mghfkmhr5adg">
              Persistent chat
            </h2>
            <p className="text-stone-medium leading-relaxed" data-id="element-658" data-mp="if5mg6zo670i">
              To make requesting help effortless, chat is accessible across the
              home screen, task overview, and task detail pages—ensuring members
              can reach the assistant without interrupting their workflow.
            </p>
          </div>
          <div className="lg:col-span-8 w-full" data-id="element-659" data-mp="3l6h61flbhy55">
            <div className="w-[85%] mx-auto rounded-md overflow-hidden" data-id="element-660" data-mp="3paglrvyrwsxg">
              <video src="https://cdn.magicpatterns.com/uploads/t8aLXLTBkWj55wn8XGTRcd/Persistent chat.mp4" autoPlay loop muted playsInline className="w-full h-auto" data-id="element-661" data-mp="2sk67sbl6o9bi" />
            </div>
          </div>
        </motion.section>
      </div>

      {/* Feature 2 — Image + Text (swapped) */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 mb-32" data-id="element-662" data-mp="31irlntfpn05y">
        <motion.section variants={item} className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center" data-id="element-663" data-mp="36cvuki8jdi9o">
          <div className="lg:col-span-4" data-id="element-664" data-mp="29swv5tg494fp">
            <h2 className="text-2xl font-bold text-ink mb-4" data-id="element-665" data-mp="2durx905vfluj">Monitor tasks</h2>
            <p className="text-stone-medium leading-relaxed" data-id="element-666" data-mp="2dsnj7pe9d6gj">
              A centralized task view helps members track progress across both
              simple and complex tasks, providing visibility into updates, next
              steps, and pending actions.
            </p>
          </div>
          <div className="lg:col-span-8 lg:order-first w-full" data-id="element-667" data-mp="1xcx63dzpwc5r">
            <div className="w-[85%] mx-auto rounded-md overflow-hidden" data-id="element-668" data-mp="1say5ucz9xvd1">
              <video src="https://cdn.magicpatterns.com/uploads/b2vfRgm6ZKqAYB7n2uvzPA/To-do.mp4" autoPlay loop muted playsInline className="w-full h-auto" data-id="element-669" data-mp="1nd6a17vidp3k" />
            </div>
          </div>
        </motion.section>
      </div>

      {/* Feature 3 — Text + Single Image */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 mb-32" data-id="element-670" data-mp="19l8bts3c5dm6">
        <motion.section variants={item} className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center" data-id="element-671" data-mp="19mt6ritkaykk">
          <div className="lg:col-span-4" data-id="element-672" data-mp="14ldilhw38bxy">
            <h2 className="text-2xl font-bold text-ink mb-4" data-id="element-673" data-mp="znbyququ88lf">
              To-do completion
            </h2>
            <p className="text-stone-medium leading-relaxed" data-id="element-674" data-mp="2zos5hre0w3qz">
              After a to-do is completed, members can provide quick feedback.
              This creates a learning loop that helps assistants better
              understand member preferences and continuously improve future
              tasks.
            </p>
          </div>
          <div className="lg:col-span-8 w-full" data-id="element-675" data-mp="2uwqu07ooih20">
            <div className="w-full rounded-md overflow-hidden" data-id="element-676" data-mp="2gyaoul6gsylz">
              <img src="https://cdn.magicpatterns.com/uploads/8cMtAyivRUwMjQAYM5ReQw/To-do_completion.png" alt="To-do completion" className="w-full h-auto" data-id="element-677" data-mp="2h0wwektcve47" />
            </div>
          </div>
        </motion.section>
      </div>

      {/* Additional Full Bleed Image Module */}
      <motion.section variants={item} className="mb-32" data-id="element-678" data-mp="2c094zy17sv9g">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center mb-12" data-id="element-679" data-mp="58rl5ndzm0pa">
          <h2 className="text-3xl font-bold text-ink mb-4" data-id="element-680" data-mp="a8cjpwp8w71h">System Design</h2>
          <p className="text-stone-medium mb-8" data-id="element-681" data-mp="a7kwb95sxfma">
            Designed an internal console and workflow system to support
            end-to-end member experiences.
          </p>
        </div>
        <div className="w-full max-w-7xl mx-auto px-6 md:px-12 space-y-8" data-id="element-682" data-mp="1pt52woepas7b">
          <div className="flex flex-col gap-3" data-id="element-683" data-mp="24h9l7r9ud1pk">
            <img src="https://cdn.magicpatterns.com/uploads/uG7VdVKweYUZQZr9EMSWxE/System_design.jpg" alt="System design — assistant desktop console and operational workflows" className="w-full h-auto block rounded-md" style={{
            maxWidth: '86.4rem'
          }} data-id="element-684" data-mp="24i1aylgrc57x" />
            <p className="text-sm text-stone-medium font-medium" data-id="element-685" data-mp="28hjt59h72272">
              Mapped end-to-end workflows to define system structure and
              processes
            </p>
          </div>
          <div className="flex flex-col gap-3" data-id="element-686" data-mp="2igius83jkxkk">
            <img src="https://cdn.magicpatterns.com/uploads/qkEYgxdkXPE3fxauyd6Zk1/Console.png" alt="Assistant desktop console" className="w-full h-auto block rounded-md" data-id="element-687" data-mp="t0rt23da1l8q" />
            <p className="text-sm text-stone-medium font-medium" data-id="element-688" data-mp="o4b1t0as6qrf">
              Designed the assistant console to manage requests, tasks, and
              progress
            </p>
          </div>
          <img src="https://cdn.magicpatterns.com/uploads/eLKv5JVVJqnYG1YL8ZJYvG/corss_platform_3.jpg" alt="Cross-platform experience — desktop and mobile" className="w-full h-auto block rounded-md mt-[64px] pt-[64px]" data-id="element-689" data-mp="1hts1hnt0y060" />
        </div>
      </motion.section>

      {/* Results Module (Dark Background) */}
      <motion.section variants={item} className="w-full bg-ink text-white py-32 px-6 md:px-12" data-id="element-690" data-mp="1wn589gju6h1d">
        <div className="max-w-4xl mx-auto text-center" data-id="element-691" data-mp="20o6y56rv5gqo">
          <h2 className="text-3xl md:text-4xl font-bold mb-8" data-id="element-692" data-mp="25ikylnldue27">
            Impact and outcome
          </h2>
          <p className="text-xl text-stone-medium font-light leading-relaxed mb-16" data-id="element-693" data-mp="2afuwlrmu7y1n">
            Successfully launched the service in Seattle, establishing the
            foundation for expansion to other cities.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-12 max-w-3xl mx-auto" data-id="element-694" data-mp="2kcfyw57h2v1s">
            <div data-id="element-695" data-mp="8siaen2hxsck">
              <div className="text-5xl font-bold mb-2" data-id="element-696" data-mp="8ougvnx1x5xu">1.1K</div>
              <div className="text-sm text-stone-medium uppercase tracking-wider" data-id="element-697" data-mp="3qj5h8r3yndi">
                Sign-ups in the first week in Seattle
              </div>
            </div>

            <div data-id="element-698" data-mp="3q03tkqfm18nn">
              <div className="text-5xl font-bold mb-2" data-id="element-699" data-mp="3q0lysck92x7p">4.7</div>
              <div className="text-sm text-stone-medium uppercase tracking-wider" data-id="element-700" data-mp="ri8394mp841u">
                App Store rating (iOS & Android)
              </div>
            </div>

            <div data-id="element-701" data-mp="mky6f3wzcq3z">
              <div className="text-5xl font-bold mb-2" data-id="element-702" data-mp="hrlv1vhbaft6">Featured </div>
              <div className="text-sm text-stone-medium uppercase tracking-wider" data-id="element-703" data-mp="hpsiy0nxj3ds">
                Fast Company, Wired, Fortune, GeekWire
              </div>
            </div>
          </div>
        </div>
      </motion.section>
    </motion.article>;
}